<template>
  <div>
    <div class="flex flex-col gap-4">
      <InputField
        v-if="extraType === VehicleExtraType.DEDUCTIBLE"
        adornment="€"
        :name="`${namePrefix}.value`"
        :label="t(`vehicleExtras.${extraType}`)"
        type="number"
        :min="0"
        :max-fraction-digits="2"
        save-number-as-string
      />
      <InputField
        v-else-if="
          !isQuantityOption &&
          [
            VehicleExtraType.ADDITIONAL_DRIVER,
            VehicleExtraType.YOUNG_DRIVER,
            VehicleExtraType.EXTRA_KM,
          ].includes(extraType as any)
        "
        :name="`${namePrefix}.value`"
        :label="t(`vehicleExtras.${extraType}`)"
        type="number"
        :min="0"
        :max-fraction-digits="0"
        save-number-as-string
      />
      <InputField
        v-else-if="isQuantityOption"
        :name="`priceRanges.${props.priceRangeIndex}.maxQuantity`"
        :label="t(`maxQuantity`)"
        type="number"
        :min="0"
        :max-fraction-digits="0"
      />
      <SelectField
        v-else
        :name="`${namePrefix}.value`"
        :label="t(`vehicleExtras.${extraType}`)"
        :options="booleanValueOptions"
      />

      <SelectField
        :name="`${namePrefix}.pricingType`"
        :label="t('pricingType')"
        :options="pricingTypeOptions"
      />
      <InputField
        v-if="pricingType === VehicleExtraOptionPricingType.FIXED"
        adornment="€"
        :name="`${namePrefix}.price`"
        :label="t('fixedPrice')"
        type="number"
        :min="0"
        :max-fraction-digits="2"
      />
      <div v-else>
        <Divider />
        <div v-if="pricePerDayFields.length" class="flex flex-col gap-4">
          <div
            v-for="(_, i) in pricePerDayFields"
            :key="i"
            class="grid grid-cols-[1fr_1fr_auto] items-end gap-4"
          >
            <InputField
              adornment="€"
              :name="`${namePrefix}.pricePerDay.${i}.fromDay`"
              :label="t('fromDay')"
              type="number"
              :min="1"
              :max-fraction-digits="2"
            />
            <InputField
              adornment="€"
              :name="`${namePrefix}.pricePerDay.${i}.pricePerDay`"
              :label="t('pricePerDay')"
              type="number"
              :min="0"
              :max-fraction-digits="2"
            />
            <button
              class="group flex size-8 items-center justify-center rounded-md hover:bg-red-400"
              @click="remove(i)"
            >
              <XMarkIcon class="text-error size-5 group-hover:text-white" />
            </button>
          </div>
        </div>
        <div v-else class="text-center">{{ t('noPrices') }}</div>
        <div class="mt-5 flex justify-center">
          <CVButton outline @click="push({ fromDay: 1, pricePerDay: 0 })"
            >+ {{ t('addPricePerDay') }}</CVButton
          >
        </div>
      </div>
    </div>
    <Divider />
    <div class="flex justify-end">
      <CVButton @click="$emit('close')">{{ t('close') }}</CVButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import InputField from '@/components/InputField.vue';
import Divider from '@/components/Divider.vue';
import { useI18n } from 'vue-i18n';
import type { EditExtraDto } from '@/views/yield-management/edit-extras/EditExtrasView.vue';
import { computed } from 'vue';
import SelectField from '@/components/SelectField.vue';
import { useField, useFieldArray } from 'vee-validate';
import {
  type PricePerDay,
  VehicleExtraOptionPricingType,
  VehicleExtraType,
} from '@carvia/ros-client-types';
import { XMarkIcon } from '@heroicons/vue/24/solid';

const props = defineProps<{
  priceRangeIndex: number;
  optionIndex: number;
  extraType: EditExtraDto['type'];
  isQuantityOption?: boolean;
}>();

defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();

const namePrefix = computed(
  () => `priceRanges.${props.priceRangeIndex}.options.${props.optionIndex}`,
);

const { value: pricingType } = useField(`${namePrefix.value}.pricingType`);
const {
  fields: pricePerDayFields,
  push,
  remove,
} = useFieldArray<PricePerDay>(`${namePrefix.value}.pricePerDay`);

const pricingTypeOptions = computed(() =>
  Object.values(VehicleExtraOptionPricingType).map((type) => ({
    label: t(type),
    value: type,
  })),
);

const booleanValueOptions = computed(() => {
  return [
    { label: t('yes'), value: 'true' },
    { label: t('no'), value: 'false' },
  ];
});
</script>

<i18n lang="json">
{
  "en": {
    "pricingType": "Pricing type",
    "FIXED": "Fixed",
    "FIXED_PER_DAY": "Per day",
    "fixedPrice": "Fixed price",
    "addPricePerDay": "Add price per day",
    "noPrices": "No prices",
    "fromDay": "From day",
    "pricePerDay": "Price per day",
    "maxQuantity": "Max quantity"
  },
  "de": {
    "pricingType": "Preistyp",
    "FIXED": "Pauschal",
    "FIXED_PER_DAY": "Pro Tag",
    "fixedPrice": "Pauschalpreis",
    "addPricePerDay": "Preis pro Tag hinzufügen",
    "noPrices": "Keine Preise",
    "fromDay": "Ab Tag",
    "pricePerDay": "Preis pro Tag",
    "maxQuantity": "Max. Anzahl"
  },
  "es": {
    "pricingType": "Tipo de precio",
    "FIXED": "Fijo",
    "FIXED_PER_DAY": "Por día",
    "fixedPrice": "Precio fijo",
    "addPricePerDay": "Añadir precio por día",
    "noPrices": "Sin precios",
    "fromDay": "A partir del día",
    "pricePerDay": "Precio por día",
    "maxQuantity": "Cantidad máx."
  }
}
</i18n>
